import { useState, useEffect } from "react";
import { useParams, useLoaderData } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useBreadcrumbs } from "../../../context/BreadcrumbsContext";
import { Delete, Eye, EyeOff, Key, Package } from "react-feather";
import { ApiKeychainSchema } from "../../../utils/types";
import ApiKeychainCard from "./ApiKeychainCard";
import { DangerZone } from "../../../components/danger-zone/DangerZone";
import { ContentButton } from "../../../components/button/ContentButton";
import { AlertModal } from "../../../components/modals/AlertModal";
import { toast } from "../../../services/toast";
import API, { ErrorWithDetails } from "../../../utils/api";
import { useNavigate } from "react-router-dom";

import { KeychainSecrets } from "../agents-chat/keychain/KeychainSecrets";
import { PackagesSearch } from "../agents-chat/packages/PackagesSearch";
import { PrettyHeader } from "../../../components/pretty-header/PrettyHeader";

import "./ApiKeychainDetailsPage.scss";
import Textbox from "../../../components/textbox/Textbox";
import { MiniEditor } from "../../../components/editor/MiniEditor";

export default function ApiKeychainDetailsPage() {

  const { apiKeychain } = useLoaderData() as { apiKeychain: ApiKeychainSchema };
  const { updateBreadcrumbs } = useBreadcrumbs();

  const [viewSecretKey, setViewSecretKey] = useState(false);
  const [isInvalidating, setIsInvalidating] = useState(false);
  const [showInvalidateAlert, setShowInvalidateAlert] = useState(false);
  const navigate = useNavigate();

  const [botPackages, setBotPackages] = useState<{[key: string]: any}>(apiKeychain.keychainConfigs?.[0]?.available_packages || {});

  useEffect(() => {
    updateBreadcrumbs([
      {
        label: apiKeychain.alias || '(No alias)',
        path: `/dashboard/api-keychains/${apiKeychain.unique_id}`,
        selected: true
      }
    ]);
    return () => {
      updateBreadcrumbs([]);
    };
  }, [apiKeychain]);

  const handleInvalidate = async () => {
    setIsInvalidating(true);
    try {
      await API.del(`v1/api_keychains`, { id: apiKeychain.unique_id });
      toast.message({ type: 'success', message: 'API keychain invalidated successfully' });
      navigate('/dashboard/api-keychains');
    } catch (err) {
      const error = err as Error;
      console.error(error);
      toast.message({
        type: 'error',
        duration: 5000,
        message: error.message
      });
      setIsInvalidating(false);
      setShowInvalidateAlert(false);
    }
  };

  const onPackagesUpdated = async ({ packages, addedPackages, removedPackages, modifiedPackages }: { packages: {[key: string]: any}, addedPackages: string[], removedPackages: string[], modifiedPackages: string[] }) => {
    const originalPackages = { ...botPackages };
    setBotPackages(packages);
    try {
      const keychainResult = await API.put(
        "v1/api_keychains",
        {
          id: apiKeychain.unique_id,
          packages
        }
      );
      for (const pkg of addedPackages) {
        toast.message({ type: 'success', message: `Package installed\n${pkg}`, duration: 3000 });
      }
      for (const pkg of removedPackages) {
        toast.message({ type: 'warning', icon: Delete, message: `Package removed\n${pkg}`, duration: 3000 });
      }
      for (const pkg of modifiedPackages) {
        toast.message({ type: 'success', message: `Package updated\n${pkg}`, duration: 3000 });
      }
    } catch (e) {
      setBotPackages(originalPackages);
      const error = e as ErrorWithDetails;
      toast.message({ type: 'error', message: `Failed to install packages: ${error.message}`, duration: 5000 });
    }
  };

  const onApiKeychainUpdated = (apiKeychain: ApiKeychainSchema) => {
    // do nothing for now, handled inside of component
  };

  return (
    <div data-component="ApiKeychainDetailsPage">
      <Helmet>
        <title>{apiKeychain.alias || "(No alias)"} - API keychain</title>
      </Helmet>
      <div className="content-container">
        <div className="item-group">
          <ApiKeychainCard apiKeychain={apiKeychain} mode="page" />
          <div className="keychain-content">
            <PrettyHeader
              title="Secret key"
              description="Use your secret key to authenticate with packages"
            />
            <div className="keychain-body">
              <div className="blurred-secret">
                <Textbox
                  size="small"
                  type={'text'}
                  monospace
                  copyable={viewSecretKey}
                  value={viewSecretKey ? apiKeychain.secret_key : Array(32).fill('•').join('')}
                  readonly
                />
                <ContentButton
                  icon={viewSecretKey ? EyeOff : Eye}
                  color="grey"
                  disabled={!apiKeychain.secret_key}
                  onClick={() => setViewSecretKey(!viewSecretKey)}
                >
                  {viewSecretKey ? 'Hide' : 'Reveal'}
                </ContentButton>
              </div>
            </div>
          </div>
          <div className="keychain-content">
            <PrettyHeader
              title="Shared secrets"
              description="Secrets this keychain can share with public packages"
            />
            <div className="keychain-body">
              <KeychainSecrets
                apiKeychain={apiKeychain}
                onApiKeychainUpdated={onApiKeychainUpdated}
              />
            </div>
          </div>
          <div className="keychain-content">
            <PrettyHeader
              title="Packages"
              description="Packages this keychain can access"
            />
            <div className="keychain-body">
              <PackagesSearch
                botPackages={botPackages}
                onPackagesUpdated={onPackagesUpdated}
              />
            </div>
          </div>
          {apiKeychain.usage_type === 'agent' && apiKeychain.agent && (
            <DangerZone>
              <div className="danger-action">
                <div className="danger-action-description">
                  <div className="title">Invalidate API keychain</div>
                  <div className="description">
                    In order to invalidate this API keychain, you must archive your agent.
                  </div>
                </div>
              </div>
            </DangerZone>
          )}
          {apiKeychain.usage_type === 'test' && apiKeychain.package && (
            <DangerZone>
              <div className="danger-action">
                <div className="danger-action-description">
                  <div className="title">Invalidate API keychain</div>
                  <div className="description">
                    In order to invalidate this API keychain, you must archive your package.
                  </div>
                </div>
              </div>
            </DangerZone>
          )}
          {(
            !apiKeychain.usage_type ||
            (apiKeychain.usage_type === 'agent' && !apiKeychain.agent) ||
            (apiKeychain.usage_type === 'test' && !apiKeychain.package)
          ) && (
            <DangerZone>
              <div className="danger-action">
                <div className="danger-action-description">
                  <div className="title">Invalidate API keychain</div>
                  <div className="description">
                    This will immediately invalidate this API keychain.<br />
                    Anything relying on this API keychain will be rendered non-functional.<br />
                    This action cannot be undone.</div>
                </div>
                <ContentButton
                  color="red"
                  onClick={() => setShowInvalidateAlert(true)}
                  loading={isInvalidating}
                >
                  Invalidate
                </ContentButton>
              </div>
            </DangerZone>
          )}
          {showInvalidateAlert && (
            <AlertModal
              message={`Invalidate this API keychain?\nThis action cannot be undone.`}
              onClose={() => setShowInvalidateAlert(false)}
              onConfirm={async (complete) => {
                await handleInvalidate();
                complete();
              }}
              onCancel={() => {}}
            />
          )}
        </div>
      </div>
    </div>
  );
} 